import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

/**
 * Global error handler for queries.
 * Logs to console in development, reports to Sentry in production.
 */
function handleQueryError(error: unknown, query: unknown) {
  // Don't report 4xx errors (expected client errors like 404s)
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status && status >= 400 && status < 500) {
    return;
  }

  // Report to Sentry in production
  if (process.env.NODE_ENV === "production") {
    Sentry.captureException(error, {
      contexts: {
        reactQuery: {
          type: "query",
          queryKey: JSON.stringify((query as { queryKey?: unknown })?.queryKey),
        },
      },
    });
  }
}

/**
 * Global error handler for mutations.
 * Logs to console in development, reports to Sentry in production.
 */
function handleMutationError(
  error: unknown,
  variables: unknown,
  _context: unknown,
  mutation: unknown
) {
  // Report to Sentry in production
  if (process.env.NODE_ENV === "production") {
    Sentry.captureException(error, {
      contexts: {
        reactQuery: {
          type: "mutation",
          mutationKey: JSON.stringify(
            (mutation as { options?: { mutationKey?: unknown } })?.options
              ?.mutationKey
          ),
          variables: JSON.stringify(variables),
        },
      },
    });
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (garbage collection)
      retry: (failureCount, error) => {
        // Don't retry 4xx errors (client errors)
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry 4xx errors (client errors)
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});
